<template>
    <card-list>
        <subheader-list-item icon="fa-address-card" title="Contact"/>
        <container-list-item padded>
            <div class="columns">
                <div class="column">
                    <!-- Company and person are separate fields deliberately:
                         the legacy single contact_name crammed
                         "Vue de Monde / Maria Rossi" into one box, which is
                         what made the old contacts unsearchable. -->
                    <!-- Autocompletes against the companies already in the
                         directory so a second contact at "Sydney Crown Towers"
                         lands under that exact name rather than a near-miss
                         spelling. Free text still works for a new company. -->
                    <b-field label="Company" label-position="inside">
                        <b-autocomplete
                                v-model="contact.company"
                                :data="companyResults"
                                field="company"
                                :loading="isFetching"
                                @typing="searchCompanies"
                                @select="selectCompany"
                                open-on-focus>

                            <template slot-scope="props">
                                <i class="fas fa-fw mr-2" :class="typeIcon(props.option.type)"></i>{{props.option.company}}
                                <br>
                                <small class="has-text-grey">
                                    {{[[props.option.city, props.option.country].filter(Boolean).join(', '),
                                       props.option.contact_count + (props.option.contact_count === 1 ? ' contact' : ' contacts')]
                                       .filter(Boolean).join(' \u00b7 ')}}
                                </small>
                            </template>
                            <template slot="empty">No matching company yet -- this will be a new one.</template>
                        </b-autocomplete>
                    </b-field>
                </div>
                <div class="column">
                    <b-field label="Type" label-position="inside">
                        <b-select v-model="contact.type" expanded>
                            <option v-for="type in types" :value="type.value" :key="type.value">{{type.label}}</option>
                        </b-select>
                    </b-field>
                </div>
            </div>
            <div class="columns">
                <div class="column">
                    <b-field label="Contact Name" label-position="inside">
                        <b-input v-model="contact.name"></b-input>
                    </b-field>
                </div>
                <div class="column">
                    <b-field label="Job Title" label-position="inside">
                        <b-input v-model="contact.title"></b-input>
                    </b-field>
                </div>
            </div>
        </container-list-item>

        <subheader-list-item icon="fa-envelope" title="Reaching them"/>
        <container-list-item padded>
            <div class="columns">
                <div class="column">
                    <b-field label="Email" label-position="inside">
                        <b-input v-model="contact.email"></b-input>
                    </b-field>
                </div>
                <div class="column">
                    <b-field label="Phone" label-position="inside">
                        <b-input v-model="contact.phone"></b-input>
                    </b-field>
                </div>
            </div>
            <div class="columns">
                <div class="column">
                    <b-field label="Second Email" label-position="inside">
                        <b-input v-model="contact.email_2"></b-input>
                    </b-field>
                </div>
                <div class="column">
                    <b-field label="Second Phone" label-position="inside">
                        <b-input v-model="contact.phone_2"></b-input>
                    </b-field>
                </div>
            </div>
            <div class="columns">
                <div class="column">
                    <b-field label="Website" label-position="inside">
                        <b-input v-model="contact.website"></b-input>
                    </b-field>
                </div>
            </div>
        </container-list-item>

        <subheader-list-item icon="fa-map-marker-alt" title="Address"/>
        <container-list-item padded>
            <div class="columns">
                <div class="column">
                    <b-field label="Address" label-position="inside">
                        <b-input type="textarea" v-model="contact.address"></b-input>
                    </b-field>
                </div>
            </div>
            <div class="columns">
                <div class="column">
                    <b-field label="City" label-position="inside">
                        <b-input v-model="contact.city"></b-input>
                    </b-field>
                </div>
                <div class="column">
                    <b-field label="Country" label-position="inside">
                        <b-input v-model="contact.country"></b-input>
                    </b-field>
                </div>
            </div>
        </container-list-item>

        <subheader-list-item icon="fa-sticky-note" title="Notes"/>
        <container-list-item padded>
            <div class="columns">
                <div class="column">
                    <!-- Directory notes only. Anything true of just one booking
                         belongs on the attachment, not here. -->
                    <b-field label="Notes about this contact" label-position="inside">
                        <b-input type="textarea" v-model="contact.notes"></b-input>
                    </b-field>
                </div>
            </div>
        </container-list-item>
    </card-list>
</template>

<script>
    import CardList from "@/TIER/components/CardList";
    import SubheaderListItem from "@/TIER/components/SubheaderListItem";
    import ContainerListItem from "@/TIER/components/ContainerListItem";
    import {TYPES, typeIcon} from "@/components/contact_meta";
    import {client as http} from "@/http_client";
    import debounce from 'debounce';

    // Company-level facts copied onto a new contact when an existing company
    // is picked. Only fills blanks: a value already typed wins.
    const COMPANY_FIELDS = ['type', 'website', 'address', 'city', 'country'];

    export default {
        name: 'ContactForm',
        components: {ContainerListItem, SubheaderListItem, CardList},
        props: {
            contact: {
                type: Object,
                required: true
            }
        },
        data: function () {
            return {
                types: TYPES,
                companyResults: [],
                isFetching: false
            };
        },
        methods: {
            typeIcon,
            // Same debounced-search shape as the airport picker on FlightForm.
            searchCompanies: debounce(function (name) {
                this.isFetching = true;
                http.get('/api/contacts/companies?q=' + encodeURIComponent(name || ''), {force: true}).then(response => {
                    this.companyResults = response.data;
                }).catch(() => {
                    this.companyResults = [];
                }).finally(() => {
                    this.isFetching = false;
                });
            }, 300),
            selectCompany(option) {
                if (!option) return;
                this.contact.company = option.company;
                COMPANY_FIELDS.forEach(key => {
                    // 'other' is the type column default, not a choice someone
                    // made, so the company's type replaces it.
                    const blank = key === 'type' ? (!this.contact[key] || this.contact[key] === 'other') : !this.contact[key];
                    if (blank && option[key]) this.$set(this.contact, key, option[key]);
                });
            }
        }
    };
</script>
